import React, { useRef, useState } from 'react';
import { BADGE_CATALOGUE } from '../../utils/badgeCatalogue';
import { useFocusTrap } from '../../hooks/use-focus-trap';

const FONT = "'Source Sans 3', system-ui, -apple-system, sans-serif";

const KEYFRAMES = `
@keyframes badge-popup-pop-in {
  0%   { opacity: 0; transform: scale(0.6) translateY(40px); }
  65%  { transform: scale(1.08) translateY(-6px); }
  80%  { transform: scale(0.97) translateY(2px); }
  100% { opacity: 1; transform: scale(1) translateY(0); }
}
@keyframes badge-popup-icon-spin {
  0%   { transform: rotateY(0deg) scale(1); }
  50%  { transform: rotateY(180deg) scale(1.1); }
  100% { transform: rotateY(360deg) scale(1); }
}
@keyframes badge-popup-sparkle {
  0%,100% { opacity: 0; transform: scale(0) rotate(0deg); }
  50%      { opacity: 1; transform: scale(1) rotate(180deg); }
}
@keyframes badge-popup-confetti-fall {
  0%   { opacity: 1; transform: translateY(-10px) rotate(0deg); }
  100% { opacity: 0; transform: translateY(80px) rotate(720deg); }
}
@keyframes badge-popup-glow-pulse {
  0%,100% { box-shadow: 0 0 20px 4px var(--badge-popup-glow); }
  50%      { box-shadow: 0 0 40px 12px var(--badge-popup-glow); }
}
@keyframes badge-popup-overlay-fade-in {
  from { opacity: 0; }
  to   { opacity: 1; }
}
@media (prefers-reduced-motion: reduce) {
  .badge-popup-overlay,
  .badge-popup-overlay * {
    animation: none !important;
    transition: none !important;
  }
  .badge-popup-confetti,
  .badge-popup-sparkle {
    display: none;
  }
}
`;

interface BadgeUnlockPopupProps {
    badgeKey: string;
    onDismiss: () => void;
}

/**
 * Celebratory "achievement unlocked" popup — confetti + sparkles + glowing badge icon.
 *
 * A modal dialog that stays until the user dismisses it (button, Escape, or a click
 * anywhere). There is deliberately no auto-dismiss timer: content that disappears on
 * its own fails WCAG 2.2.1 for anyone who reads or navigates slowly. TopHeader advances
 * its badge queue on onDismiss, so queued badges still show one after another.
 */
function BadgeUnlockPopup({ badgeKey, onDismiss }: BadgeUnlockPopupProps) {
    const info = BADGE_CATALOGUE.find(b => b.key === badgeKey) ?? BADGE_CATALOGUE[0];
    const [dismissing, setDismissing] = useState(false);
    const dismissingRef = useRef(false);
    const cardRef = useRef<HTMLDivElement>(null);
    const dismissButtonRef = useRef<HTMLButtonElement>(null);

    function handleDismiss() {
        // Button click, Escape and the backdrop can all fire; advance the queue once.
        if (dismissingRef.current) return;
        dismissingRef.current = true;
        setDismissing(true);
        setTimeout(onDismiss, 350);
    }

    useFocusTrap(cardRef, true, { onEscape: handleDismiss, initialFocusRef: dismissButtonRef });

    const confettiColors = ['#f59e0b', '#10b981', '#3b82f6', '#ef4444', '#8b5cf6', '#f97316'];
    const confetti = Array.from({ length: 18 }, (_, i) => ({
        color: confettiColors[i % confettiColors.length],
        left: `${10 + (i * 5) % 80}%`,
        delay: `${(i * 0.12).toFixed(2)}s`,
        size: `${6 + (i % 4) * 3}px`,
        duration: `${0.9 + (i % 3) * 0.3}s`,
    }));

    const sparkles = Array.from({ length: 8 }, (_, i) => ({
        angle: i * 45,
        delay: `${(i * 0.1).toFixed(1)}s`,
    }));

    return (
        // Click-anywhere-to-dismiss is a mouse convenience; keyboard users get Escape and the button.
        // Only a click on the backdrop itself dismisses (the confetti layer is pointer-events: none),
        // so the card needs no stopPropagation.
        <div
            className="badge-popup-overlay"
            role="presentation"
            onClick={(e) => { if (e.target === e.currentTarget) handleDismiss(); }}
            style={{
                position: 'fixed', inset: 0, zIndex: 9999,
                background: 'rgba(0,0,0,0.55)',
                backdropFilter: 'blur(4px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                animation: dismissing ? 'none' : 'badge-popup-overlay-fade-in 0.25s ease',
                opacity: dismissing ? 0 : 1,
                transition: dismissing ? 'opacity 0.35s ease' : 'none',
                cursor: 'pointer',
                fontFamily: FONT,
            }}
        >
            <style>{KEYFRAMES}</style>

            {/* Confetti */}
            <div className="badge-popup-confetti" aria-hidden="true" style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
                {confetti.map((c, i) => (
                    <div key={i} style={{
                        position: 'absolute', top: '20%', left: c.left,
                        width: c.size, height: c.size,
                        background: c.color, borderRadius: 2,
                        animation: `badge-popup-confetti-fall ${c.duration} ${c.delay} ease-in forwards`,
                    }} />
                ))}
            </div>

            {/* Card */}
            <div
                ref={cardRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby="badge-unlock-popup-title"
                aria-describedby="badge-unlock-popup-desc"
                style={{
                    background: '#fff',
                    borderRadius: 24,
                    padding: '44px 40px 36px',
                    textAlign: 'center',
                    maxWidth: 360,
                    width: '90%',
                    boxShadow: '0 32px 80px rgba(0,0,0,0.35)',
                    animation: dismissing ? 'none' : 'badge-popup-pop-in 0.55s cubic-bezier(0.34,1.56,0.64,1) forwards',
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                {/* Gradient top strip */}
                <div style={{
                    position: 'absolute', top: 0, left: 0, right: 0, height: 6,
                    background: info.grad,
                    borderRadius: '24px 24px 0 0',
                }} />

                {/* Visual eyebrow; the dialog title carries "Badge unlocked" for screen readers. */}
                <div aria-hidden="true" style={{ fontSize: '0.7rem', fontWeight: 800, color: info.color, textTransform: 'uppercase', letterSpacing: '0.15em', marginBottom: 20 }}>
                    🎉 Badge Unlocked!
                </div>

                {/* Badge icon with sparkles */}
                <div aria-hidden="true" style={{ position: 'relative', width: 110, height: 110, margin: '0 auto 22px' }}>
                    {/* Sparkle arms */}
                    {sparkles.map((s, i) => (
                        <div key={i} className="badge-popup-sparkle" style={{
                            position: 'absolute', top: '50%', left: '50%',
                            width: 8, height: 8,
                            marginTop: -4, marginLeft: -4,
                            transformOrigin: `4px ${52 + i * 2}px`,
                            transform: `rotate(${s.angle}deg)`,
                        }}>
                            <div style={{
                                width: 8, height: 8,
                                background: info.color,
                                borderRadius: '50%',
                                transform: `translateY(-${52 + i * 2}px)`,
                                animation: `badge-popup-sparkle 1.2s ${s.delay} ease-in-out infinite`,
                            }} />
                        </div>
                    ))}

                    {/* Icon circle */}
                    <div style={{
                        position: 'absolute', inset: 8,
                        borderRadius: '50%',
                        background: info.grad,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '2.8rem',
                        // @ts-ignore
                        '--badge-popup-glow': `${info.color}66`,
                        animation: 'badge-popup-glow-pulse 2s ease-in-out infinite, badge-popup-icon-spin 1s cubic-bezier(0.34,1.56,0.64,1) forwards',
                        boxShadow: `0 0 20px 4px ${info.color}55`,
                    }}>
                        {info.icon}
                    </div>
                </div>

                <h2 id="badge-unlock-popup-title" style={{ fontSize: '1.3rem', fontWeight: 800, color: '#0f172a', margin: '0 0 8px', lineHeight: 'normal', fontFamily: FONT }}>
                    <span className="sr-only">Badge unlocked: </span>
                    {info.name}
                </h2>
                <div id="badge-unlock-popup-desc" style={{ fontSize: '0.88rem', color: '#475569', lineHeight: 1.6, marginBottom: 28 }}>
                    {info.desc}
                </div>

                <button
                    ref={dismissButtonRef}
                    type="button"
                    onClick={handleDismiss}
                    style={{
                        background: info.color,
                        color: '#fff',
                        border: 'none',
                        borderRadius: 99,
                        padding: '10px 32px',
                        fontSize: '0.88rem',
                        fontWeight: 700,
                        cursor: 'pointer',
                        boxShadow: `0 4px 16px ${info.color}55`,
                        fontFamily: FONT,
                        transition: 'opacity 0.15s',
                    }}
                >
                    Awesome!
                </button>

                <div aria-hidden="true" style={{ fontSize: '0.65rem', color: '#64748b', marginTop: 12 }}>
                    Click anywhere or press Esc to dismiss
                </div>
            </div>
        </div>
    );
}

export default BadgeUnlockPopup;
